import { useState } from 'react'
import { InfoOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from '@/helpers'
import { UserProfile } from '@/types'
import { saveProfile } from '@/pages/session/profile/save-profile'
import { GRADUATION_MONTHS } from '@/pages/session/profile/profile.constants'
import {
  estimatedSemester,
  graduationDate,
} from '@/pages/session/profile/profile-steps'
import { CreditProfileKind, profileName } from '../products/credit-profile'

const now = new Date()
const currentYear = now.getFullYear()
const currentMonth = now.getMonth()
const minYear = currentYear
const maxYear = currentYear + 7
const years = Array.from(
  { length: maxYear - minYear + 1 },
  (_, index) => minYear + index,
)

type ProfileDialogProps = {
  open: boolean
  onClose: () => void
  user: UserProfile | undefined
  profile: CreditProfileKind
  onUpdateToDoctor: () => void
}

function readGraduation(user: UserProfile | undefined) {
  const value = user?.profile?.graduation_date
  if (!value) return { year: '', month: '' }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { year: '', month: '' }
  return {
    year: String(date.getUTCFullYear()),
    month: String(date.getUTCMonth()),
  }
}

export function ProfileDialog({
  open,
  onClose,
  user,
  profile,
  onUpdateToDoctor,
}: ProfileDialogProps) {
  const initial = readGraduation(user)
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState(initial.month)
  const [saving, setSaving] = useState(false)

  const isStudent = profile === 'student'
  const selectedYear = year === '' ? null : Number(year)
  const selectedMonth = month === '' ? null : Number(month)
  const monthEnabled = (index: number) =>
    !(Number(year) === currentYear && index <= currentMonth)
  const canSave =
    selectedYear != null && selectedMonth != null && monthEnabled(selectedMonth)
  const graduationInThePast =
    selectedYear != null &&
    selectedMonth != null &&
    (selectedYear < currentYear ||
      (selectedYear === currentYear && selectedMonth <= currentMonth))
  const semesterLabel =
    selectedYear == null || selectedMonth == null
      ? 'Informe a data de formatura'
      : graduationInThePast
        ? 'Formado'
        : `${estimatedSemester(selectedYear, selectedMonth)}º semestre`

  const crm = user?.profile?.crm
  const crmUf = user?.profile?.crm_uf
  const profileValue =
    profile === 'doctor' && crm && crmUf
      ? `${profileName(profile)} · CRM-${crmUf} ${crm}`
      : profileName(profile)

  async function save() {
    if (!canSave || saving) return
    setSaving(true)
    try {
      await saveProfile({
        graduation_date: graduationDate(Number(year), Number(month)),
      })
      onClose()
    } catch {
      toast.error('Não foi possível salvar a data de formatura.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="credit-profile-title"
    >
      <DialogTitle id="credit-profile-title">Seu perfil</DialogTitle>
      <DialogContent>
        <TextField
          label="Perfil"
          value={profileValue}
          fullWidth
          slotProps={{ input: { readOnly: true } }}
          sx={{ mt: 1 }}
        />

        {isStudent && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                mt: 2,
              }}
            >
              <TextField
                select
                label="Mês de formatura"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              >
                {GRADUATION_MONTHS.map((label, index) => (
                  <MenuItem
                    key={label}
                    value={String(index)}
                    disabled={!monthEnabled(index)}
                  >
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Ano de formatura"
                value={year}
                onChange={(event) => setYear(event.target.value)}
              >
                {years.map((option) => (
                  <MenuItem key={option} value={String(option)}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              label="Semestre atual"
              value={semesterLabel}
              fullWidth
              slotProps={{ input: { readOnly: true } }}
              sx={{ mt: 2 }}
            />

            <Typography variant="body2" color="text.secondary" mt={1.5}>
              Calculamos o semestre pela data de formatura, considerando um
              curso de 12 semestres. O Financiamento Estudantil fica disponível
              a partir do 8º semestre e a Mesada a partir do 9º.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <InfoOutlined color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary" flex={1}>
                Já se formou? Atualize para Médico com o seu CRM.
              </Typography>
              <Button size="small" onClick={onUpdateToDoctor}>
                Atualizar
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isStudent ? (
          <LoadingButton loading={saving} disabled={!canSave} onClick={save}>
            Salvar
          </LoadingButton>
        ) : (
          <Button onClick={onClose}>Fechar</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
